import { useEffect } from "react";
import { layoutOf } from "../utils/selfcheckoutMode";

/**
 * Identidade da loja no navegador do celular:
 * - `theme-color`: a barra do navegador fica na cor da loja;
 * - manifest por loja: "Adicionar à tela inicial" instala com o nome e o logo
 *   da loja e abre direto no cardápio dela (`start_url`);
 * - `apple-touch-icon`: o ícone que o iPhone usa ao adicionar à tela inicial.
 *
 * Tudo em runtime, no <head>: o index.html não muda, e o contrato da function
 * `ssr` (que monta o <head> por substituição de texto) fica intacto. O
 * manifest vai como data: URL, então os endereços dentro dele são absolutos.
 * Fora do celular (totem, POS, tablet) nada disso se aplica.
 */
export default function useShopManifest(shop, namespace) {
  const loading = shop.name === "";
  const isMobileUi = !loading && layoutOf(shop).mobileUi;
  const { name, backgroudColor, pictureUrl } = shop;

  useEffect(() => {
    if (!isMobileUi || !namespace) return;
    const color = backgroudColor || "#dc3545";
    const origin = window.location.origin;
    const startUrl = `${origin}/${namespace}/`;

    const themeMeta = document.querySelector('meta[name="theme-color"]');
    const previousTheme = themeMeta ? themeMeta.getAttribute("content") : null;
    if (themeMeta) themeMeta.setAttribute("content", color);

    const manifest = {
      // cada loja é um app à parte na tela inicial
      id: startUrl,
      name,
      short_name: name.length > 12 ? name.slice(0, 12).trim() : name,
      start_url: startUrl,
      scope: startUrl,
      display: "standalone",
      background_color: "#ffffff",
      theme_color: color,
      // O logo cadastrado da loja. O Chrome só considera a loja instalável (e
      // só dispara o beforeinstallprompt) com ícones declarados de 192 e
      // 512px; "any" vale só para SVG. O logo é um arquivo só e o navegador
      // redimensiona. Não há ícone padrão da Pallas no public/: sem logo, a
      // loja não é instalável no Android e o card de instalar não aparece.
      icons: pictureUrl
        ? ["192x192", "512x512"].map((sizes) => ({
            src: pictureUrl,
            sizes,
            purpose: "any",
          }))
        : [],
    };

    const added = [];
    const addLink = (rel, href) => {
      const link = document.createElement("link");
      link.rel = rel;
      link.href = href;
      document.head.appendChild(link);
      added.push(link);
    };
    addLink(
      "manifest",
      `data:application/manifest+json,${encodeURIComponent(
        JSON.stringify(manifest),
      )}`,
    );
    if (pictureUrl) addLink("apple-touch-icon", pictureUrl);

    return () => {
      added.forEach((link) => link.remove());
      if (themeMeta && previousTheme !== null) {
        themeMeta.setAttribute("content", previousTheme);
      }
    };
  }, [isMobileUi, namespace, name, backgroudColor, pictureUrl]);
}
